import React, { useId } from 'react';
import styled, { css } from 'styled-components';
import { MobileAppFlair } from 'types/graphql';
import {
  CheckerboardPatternDefs,
  FLAIR_VIEWBOX,
  getCheckerboardPatternId,
  getFlairFill,
  getFlairPath,
} from './get-flair-path';

type FlairWrapperProps = {
  flairType: MobileAppFlair;
  color: string;
  children: React.ReactNode;
};

/**
 * Wraps a mobile app grid section with a colored background and a matching flair
 * edge above and below the content.
 */
export function FlairWrapper({ flairType, color, children }: FlairWrapperProps): JSX.Element {
  const instanceId = useId();
  const path = getFlairPath(flairType);

  if (flairType === MobileAppFlair.none || !path) {
    return <>{children}</>;
  }

  const patternId = getCheckerboardPatternId(instanceId, color);
  const fill = getFlairFill(flairType, color, patternId);

  return (
    <Wrapper>
      {/* Rendered once (not once per edge svg below) -- ids are document-global, so a single
          defs here covers both the top and bottom edge's fill references to `patternId`. `<defs>`
          never paints anything on its own, so it's cheap to always render rather than gating it
          behind a checkerBoard check. */}
      <HiddenSvg aria-hidden='true'>
        <CheckerboardPatternDefs color={color} patternId={patternId} />
      </HiddenSvg>
      <FlairEdge aria-hidden='true'>
        <svg viewBox={FLAIR_VIEWBOX} preserveAspectRatio='none'>
          <path d={path} fill={fill} />
        </svg>
      </FlairEdge>
      <Content $color={color}>{children}</Content>
      <FlairEdge aria-hidden='true' $flipped $rotated={flairType === MobileAppFlair.angled}>
        <svg viewBox={FLAIR_VIEWBOX} preserveAspectRatio='none'>
          <path d={path} fill={fill} />
        </svg>
      </FlairEdge>
    </Wrapper>
  );
}

const HiddenSvg = styled.svg`
  height: 0;
  position: absolute;
  width: 0;
`;

const Wrapper = styled.div`
  width: 100%;
  margin-bottom: 40px;
`;

const Content = styled.div<{ $color: string }>`
  background: ${({ $color }) => $color};
`;

const FlairEdge = styled.div<{ $flipped?: boolean; $rotated?: boolean }>`
  height: 24px;
  line-height: 0;
  width: 100%;

  ${({ $flipped }) =>
    $flipped &&
    css`
      transform: scaleY(-1);
    `}
  ${({ $flipped, $rotated }) =>
    $flipped &&
    $rotated &&
    css`
      transform: scaleX(-1) scaleY(-1);
    `}

  svg {
    display: block;
    height: 100%;
    width: 100%;
  }
`;
